<template>
  <div class="calculator" :id="calculatorName">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-10 col-xl-8 mb-3">
          <div class="progress-container">
          <div class="progress-bar-bg">
            <div class="progress-bar-fill" :style="{ width: ((currentStep - 1) / (totalSteps - 1)) * 100 + '%' }"></div>
          </div>
        </div>

        <div class="step-nav-buttons mt-4 text-center">
              <button
                type="button"
                class="btn previous-step"
                @click="prevStep"
                v-if="currentStep != 1"
              >
                Back
              </button>
        </div>

        <div class="calculator-container">

          <div class="mt-3" @keyup="resetCalculation" @change="resetCalculation">

            <div v-if="currentStep === 1">
              <InputElement
                  
                  fieldText="Estimated Property Value"
              >
                <InputTypeNumber currencyPlaceholder placeholder="e.g. 230000" v-model="propertyValue"/>
              </InputElement>
            </div>

            <div v-if="currentStep === 2">
              <InputElement
                  
                  fieldText="Outstanding Mortgage"
              >
                <InputTypeNumber currencyPlaceholder placeholder="e.g. 30000" v-model="outstanding"/>
              </InputElement>
            </div>

            <div v-if="currentStep === 3">
              <BooleanChoiceButtons
                  fieldText="Number of Applicants"
                  buttonTextLeft="1"
                  buttonTextRight="2"
              >
                <template v-slot:left>
                  <input type="radio" v-model="applicantsQty" value="1">
                </template>
                <template v-slot:right>
                  <input type="radio" v-model="applicantsQty" value="2">
                </template>
              </BooleanChoiceButtons>
            </div>

            <div v-if="currentStep === 4">
              <InputElement
                  fieldText="Age of Youngest Homeowner"
                  class="pt-4"
              >
                <VueSlider
                    ref="slider"
                    v-model="age"
                    v-bind="sliderOptions"
                    class="mt-4 mt-md-0"
                    @keyup="resetCalculation" @change="resetCalculation"
                >
                  <template #tooltip>
                    <div style="min-width: 6rem; font-size: small;" v-if="age === null ">Set your age</div>
                    <div v-if="age >= 55 && age <= 89">{{ age }}</div>
                    <div v-if="age > 89">90+</div>
                  </template>
                </VueSlider>
              </InputElement>
            </div>

            <div class="row mb-5" v-if="currentStep === 5">
              <div class="col-12 booleanLabel">When will you be ready to move forward?</div>
              <div class="col-12 row calc-right-buttons-wrapper">
                <MultiplyChoiceButton
                    buttonText="As soon as possible"
                >
                  <input type="radio" v-model="calcReason2" value="As soon as possible">
                </MultiplyChoiceButton>
                <MultiplyChoiceButton
                    buttonText="In the next 3 months"
                >
                  <input type="radio" v-model="calcReason2" value="In the next 3 months">
                </MultiplyChoiceButton>
                <MultiplyChoiceButton
                    buttonText="6-12 months from now"
                >
                  <input type="radio" v-model="calcReason2" value="6-12 months from now">
                </MultiplyChoiceButton>
                <MultiplyChoiceButton
                    buttonText="12+ months from now"
                >
                  <input type="radio" v-model="calcReason2" value="12+ months from now">
                </MultiplyChoiceButton>
                </div>
            </div>

              <div class="step-nav-buttons mt-4 text-center">
                <button
                  type="button"
                  class="btn next-step"
                  @click="nextStep"
                  v-if="currentStep != totalSteps - 1 && currentStep != 6"
                  :disabled="!isStepValid"
                >
                  Continue
                </button>

                <button
                  type="button"
                  class="btn calculate"
                  v-if="currentStep === totalSteps - 1"
                  @click="calculate"
                  :disabled="!isStepValid"
                >
                  Calculate
                </button>
              </div>
          </div>

          <div v-if="currentStep === 6" ref="resultsContainer">

            <ResultsContainer class="mt-5 mb-5 calc-results-box">
              <div class="mb-3 results-title">
                <ul class="text-smaller">
                  <li>Draw a tax-free lump sum and spend it as you please</li>
                  <li>Retain ownership of your property at all times</li>
                  <li>We're Members of the Equity Release Council</li>
                  <li>Protect your beneficiaries</li>
                </ul>
                <b class="text-uppercase mt-3">The figures are an indication of the amount available, subject to
                  full assessment and underwriting. Please refer to one of our specialist advisers.</b>
              </div>
              <div class="mb-3">Thank you for your details. Please see below for the maximum amounts you can
                release based on the information you have provided
              </div>
              <div class="mb-3 results-title">Lifetime Mortgage:</div>
              <span class="fcs-price">£{{ formatNumberOutput(lifetimeMortgage) }}</span>
              <div class="mb-3 results-title">Enhanced Lifetime Mortgage *:</div>
              <span class="fcs-price">£{{ formatNumberOutput(enhancedLifetimeMortgage) }}</span>
              <div class="text-smaller">* If you are in poor health you may be entitled to better terms with an
                Enhanced Lifetime Mortgage.
              </div>
            </ResultsContainer>

            <SubmissionForm :calculatorName="calculatorName" :calculatorData="collateMessageBody"/>

          </div>
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BooleanChoiceButtons from '@/components/utilities/booleanChoiceButtons.vue'
import InputElement from '@/components/utilities/inputElement.vue'
import InputTypeNumber from '@/components/utilities/inputTypeNumber.vue'
import MultiplyChoiceButton from '@/components/utilities/multiplyChoiceButton.vue'
import ResultsContainer from '@/components/utilities/resultsContainer.vue'
import SubmissionForm from '@/components/utilities/submissionForm.vue'
import axios from "axios"

import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/antd.css'

export default {
  components: {
    BooleanChoiceButtons,
    InputElement,
    InputTypeNumber,
    MultiplyChoiceButton,
    ResultsContainer,
    SubmissionForm,
    VueSlider
  },
  data() {
    return {
      currentStep: 1,
      totalSteps: 6,
      calculatorName: 'equity-release-calculator',
      calcCompleted: false,
      lifetimeMortgaget: null,
      enhancedLifetimeMortgage: null,
      homeReversion: 0,
      propertyValue: null,
      outstanding: null,
      applicantsQty: 0,
      age: null,
      calculatorReasonTitle: null,
      calculatorReasonResults: null,
      calcReason2: "",
      listName: "",
      emailAddress: "",
      consent: "",
      calculation:
          {
            "55": {
              "standard": 25.5,
              "enhanced": 39.1,
              "interest": 21
            },
            "56": {
              "standard": 26.5,
              "enhanced": 40.2,
              "interest": 22
            },
            "57": {
              "standard": 27.5,
              "enhanced": 41.3,
              "interest": 23
            },
            "58": {
              "standard": 28.5,
              "enhanced": 42.4,
              "interest": 24
            },
            "59": {
              "standard": 29.5,
              "enhanced": 43.5,
              "interest": 25
            },
            "60": {
              "standard": 32,
              "enhanced": 44.6,
              "interest": 29
            },
            "61": {
              "standard": 33,
              "enhanced": 45.7,
              "interest": 30
            },
            "62": {
              "standard": 34,
              "enhanced": 47.3,
              "interest": 31
            },
            "63": {
              "standard": 35,
              "enhanced": 48.4,
              "interest": 32
            },
            "64": {
              "standard": 36,
              "enhanced": 49.5,
              "interest": 33
            },
            "65": {
              "standard": 37,
              "enhanced": 50.6,
              "interest": 34
            },
            "66": {
              "standard": 38,
              "enhanced": 51,
              "interest": 35
            },
            "67": {
              "standard": 39,
              "enhanced": 51.8,
              "interest": 36
            },
            "68": {
              "standard": 40,
              "enhanced": 52.9,
              "interest": 37
            },
            "69": {
              "standard": 41,
              "enhanced": 54,
              "interest": 38
            },
            "70": {
              "standard": 42,
              "enhanced": 54.5,
              "interest": 39
            },
            "71": {
              "standard": 43.2,
              "enhanced": 55.5,
              "interest": 40
            },
            "72": {
              "standard": 44.4,
              "enhanced": 55.5,
              "interest": 41
            },
            "73": {
              "standard": 45.6,
              "enhanced": 55.5,
              "interest": 42
            },
            "74": {
              "standard": 46.8,
              "enhanced": 55.5,
              "interest": 43
            },
            "75": {
              "standard": 48,
              "enhanced": 55.5,
              "interest": 44
            },
            "76": {
              "standard": 49.2,
              "enhanced": 55.5,
              "interest": 45
            },
            "77": {
              "standard": 50.4,
              "enhanced": 55.5,
              "interest": 46
            },
            "78": {
              "standard": 51,
              "enhanced": 55.5,
              "interest": 47
            },
            "79": {
              "standard": 51.5,
              "enhanced": 55.5,
              "interest": 48
            },
            "80": {
              "standard": 52,
              "enhanced": 55.5,
              "interest": 49
            },
            "81": {
              "standard": 52.5,
              "enhanced": 55.5,
              "interest": 49
            },
            "82": {
              "standard": 53,
              "enhanced": 55.5,
              "interest": 49
            },
            "83": {
              "standard": 53.5,
              "enhanced": 55.5,
              "interest": 49
            },
            "84": {
              "standard": 54,
              "enhanced": 55.5,
              "interest": 49
            },
            "85": {
              "standard": 54,
              "enhanced": 55.5,
              "interest": 49
            },
            "86": {
              "standard": 54,
              "enhanced": 55.5,
              "interest": 49
            },
            "87": {
              "standard": 54,
              "enhanced": 55.5,
              "interest": 49
            },
            "88": {
              "standard": 54,
              "enhanced": 55.5,
              "interest": 49
            },
            "89": {
              "standard": 54,
              "enhanced": 55.5,
              "interest": 49
            },
            "90": {
              "standard": 54,
              "enhanced": 55.5,
              "interest": 49
            }
          },
      sliderOptions: {
        min: 55,
        max: 90,
      },
      submissionReason: {
        submitted: false,
        is_valid: false,
        confirmation_message: ''
      },
      submissionList: {
        submitted: false,
        is_valid: false,
        confirmation_message: ''
      },
      formErrors: [],
      gfid: 6, //Please check the Gravity Form for corresponding form id (staging and live may vary!)
      gfidlist: 9 //Please check the Gravity Form for corresponding form id (staging and live may vary!)
    }
  },
  methods: {
    calculate() {
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
      }
      let match = this.calculation[this.age];

      this.lifetimeMortgage = this.propertyValue * (match.standard / 100);
      this.enhancedLifetimeMortgage = this.propertyValue * (match.enhanced / 100);
      // this.homeReversion = this.propertyValue * (match.interest / 100);
      this.calcCompleted = true;
      this.calculatorReasonTitle = this.removeHyphensToUpperCase(this.calculatorName);
      this.calculatorReasonResults =
          `
      When will you be ready to move forward: ${this.calcReason2}
      `;
      this.calculatorListName = this.listName;
      this.calculatorEmailAddress = this.emailAddress;
      this.calculatorConsent = this.consent;
      this.$nextTick(() => {
        const resultsContainer = this.$refs.resultsContainer;

        if (resultsContainer) {
          // Get the bounding rectangle of the element
          const rect = resultsContainer.getBoundingClientRect();

          // Calculate the target scroll position
          const targetScrollPosition = window.scrollY + rect.top - 200;

          // Scroll to the target position
          window.scrollTo({
            top: targetScrollPosition,
            behavior: 'smooth'
          });
        } else {
          console.error('resultsContainer is undefined');
        }
      });
      var newReasonSubmission = new FormData();
      var newListSubmission = new FormData();

      newReasonSubmission.append("form_id", this.gfid); //Please check the Gravity Form form id (staging and live may vary!)
      newReasonSubmission.append("input_1", this.calculatorReasonTitle);
      newReasonSubmission.append("input_3", this.calculatorReasonResults);

      newListSubmission.append("form_id", this.gfidlist); //Please check the Gravity Form form id (staging and live may vary!)
      newListSubmission.append("input_1", this.calculatorListName);
      newListSubmission.append("input_3", this.calculatorEmailAddress);
      newListSubmission.append("input_4.1", this.calculatorConsent ? 1 : 0);

      this.postReasonSubmission(newReasonSubmission);
      this.postListSubmission(newListSubmission);
    },
    async postReasonSubmission(submissionReasonData) {
      try {
        const response = axios.post('/wp-json/gf/v2/forms/8/submissions', submissionReasonData, {
          headers: {
            "Content-type": "application/json; charset=UTF-8",
          },
        });
        const submissionReason = response;
        
        this.submissionReason.is_valid = submissionReason.is_valid;
        this.submissionReason.confirmation_message = submissionReason.confirmation_message;
        this.submissionReason.validation_messages = submissionReason.validation_messages;

      } catch(error) {
        console.log(error)
      }
    },
    async postListSubmission(submissionListData) {
      try {
        const response = axios.post('/wp-json/gf/v2/forms/9/submissions', submissionListData, {
          headers: {
            "Content-type": "application/json; charset=UTF-8",
          },
        });
        const submissionList = response;
        
        this.submissionList.is_valid = submissionList.is_valid;
        this.submissionList.confirmation_message = submissionList.confirmation_message;
        this.submissionList.validation_messages = submissionList.validation_messages;

      } catch(error) {
        console.log(error)
      }
    },
    formatNumberOutput(number) {
      const newNumber = (new Intl.NumberFormat('en-GB').format(number))
      if (newNumber === 'NaN') {
        return 0
      }
      return newNumber
    },
    removeHyphensToUpperCase(str) {
      return str.replace(/(-|^)([^-]?)/g, function (_, prep, letter) {
        return (prep && ' ') + letter.toUpperCase();
      });
    },
    resetCalculation() {
      this.calcCompleted = false;
    },
    nextStep() {
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep === 6) {
        this.currentStep = 5;
      } else if (this.currentStep < this.totalSteps) {
        this.currentStep--;
      }
    },
  },
  computed: {
    isStepValid() {
      if (this.currentStep === 1) {
        return this.propertyValue != null;
      }
      if (this.currentStep === 2) {
        return this.outstanding != null;
      }
      if (this.currentStep === 3) {
        return this.applicantsQty === '1' || this.applicantsQty === '2';
      }
      if (this.currentStep === 4) {
        return this.age != null;
      }
      if (this.currentStep === 5) {
        return this.calcReason2 != null;
      }
      return true;
    },
    collateMessageBody() {
      const calculatorData =
          {
            calculatorTitle: this.removeHyphensToUpperCase(this.calculatorName),
            calculatorResults:
                `
                    Estimated Property Value: £${this.propertyValue}
                    Outstanding Mortgage: £${this.outstanding}
                    Applicants: ${this.applicantsQty}
                    Age of Youngest Homeowner: ${this.age}
                    Lifetime Mortgage: £${this.lifetimeMortgage}
                    Enhanced Lifetime Mortgage: £${this.enhancedLifetimeMortgage}
                `
          }
      return calculatorData;
    }
  }
}
</script>

<style lang="scss">
@import './styles/calcs.scss';
@import './styles/slider.scss';
</style>